import { useRef, useState } from "react";
import { useT } from "../i18n";

export interface DemandValue {
  mode: "text" | "audio" | "files";
  text?: string;
  transcript?: string;
  fileNames?: string[];
}

export function DemandInput({
  value,
  onChange,
}: {
  value: DemandValue;
  onChange: (v: DemandValue) => void;
}) {
  const { t } = useT();
  const [recording, setRecording] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setMode = (mode: DemandValue["mode"]) => onChange({ ...value, mode });

  // Mocked recording: real MediaRecorder + OpenAI transcription lands with the backend spec.
  const toggleRecording = () => {
    if (recording) {
      setRecording(false);
      onChange({
        ...value,
        transcript:
          value.transcript ??
          "(mock transcript) Describe your automation need — this will be real Whisper output once the backend is wired.",
      });
    } else {
      setRecording(true);
    }
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const names = [...files].map((f) => f.name);
    onChange({ ...value, fileNames: [...(value.fileNames ?? []), ...names] });
  };

  const removeFile = (name: string) => {
    onChange({ ...value, fileNames: (value.fileNames ?? []).filter((n) => n !== name) });
  };

  return (
    <div>
      <div className="demand-tabs">
        <button
          type="button"
          className={value.mode === "text" ? "active" : ""}
          onClick={() => setMode("text")}
        >
          {t("demandTabText")}
        </button>
        <button
          type="button"
          className={value.mode === "audio" ? "active" : ""}
          onClick={() => setMode("audio")}
        >
          {t("demandTabAudio")}
        </button>
        <button
          type="button"
          className={value.mode === "files" ? "active" : ""}
          onClick={() => setMode("files")}
        >
          {t("demandTabFiles")}
        </button>
      </div>

      {value.mode === "text" && (
        <textarea
          className="demand-textarea"
          placeholder={t("demandTextPlaceholder")}
          value={value.text ?? ""}
          onChange={(e) => onChange({ ...value, text: e.target.value })}
        />
      )}

      {value.mode === "audio" && (
        <div>
          <button className="btn primary" type="button" onClick={toggleRecording}>
            {recording ? t("demandRecordStop") : t("demandRecordStart")}
          </button>
          <div className="wizard-step-label" style={{ marginTop: 16 }}>
            {t("demandTranscriptLabel")}
          </div>
          <textarea
            className="demand-textarea"
            placeholder={t("demandTranscriptPlaceholder")}
            value={value.transcript ?? ""}
            onChange={(e) => onChange({ ...value, transcript: e.target.value })}
          />
        </div>
      )}

      {value.mode === "files" && (
        <div>
          <div
            className={`dropzone${dragOver ? " drag" : ""}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            {t("demandDropHint")}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => addFiles(e.target.files)}
          />
          {(value.fileNames ?? []).length > 0 && (
            <ul className="filelist">
              {value.fileNames!.map((name) => (
                <li key={name}>
                  <span>{name}</span>
                  <button className="btn ghost" type="button" onClick={() => removeFile(name)}>
                    {t("demandFileRemove")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
